/**
 * RFC 1035 compliant domain validation regex.
 *
 * Rules per RFC 1035:
 * - Each label (part between dots) must:
 *   - Start and end with alphanumeric characters
 *   - Be 1-63 characters long
 *   - Can contain hyphens in the middle (but not at start/end)
 * - TLD (top-level domain) must be at least 2 characters
 * - Must contain at least one dot
 * - Underscores are NOT allowed (common misconception)
 *
 * Note: When using domain examples in UI (placeholders, documentation, etc.),
 * use RFC 2606 reserved domains:
 * - example.com, example.net, example.org (and their subdomains)
 * - *.test, *.localhost, *.invalid (for testing)
 */
const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

/**
 * Validates if a string is a valid domain name according to RFC 1035.
 *
 * @param domain - The domain string to validate
 * @returns true if the domain is valid, false otherwise
 *
 * @example
 * isValidDomain("example.com") // true
 * isValidDomain("sub.example.com") // true
 * isValidDomain("my-site.co.uk") // true
 * isValidDomain("-invalid.com") // false (starts with hyphen)
 * isValidDomain("invalid-.com") // false (ends with hyphen)
 * isValidDomain("no-tld") // false (no TLD)
 * isValidDomain("my_site.com") // false (underscore not allowed per RFC 1035)
 */
export function isValidDomain(domain: string): boolean {
    if (!domain || domain.trim().length === 0) {
        return false;
    }
    return DOMAIN_REGEX.test(domain.trim());
}

// localhost with optional port — matches the pattern in desktop's domain-utils.ts
const LOCALHOST_REGEX = /^(?:localhost|127\.0\.0\.1)(?::\d{1,5})?$/;

/**
 * Validates a bare domain, optionally allowing localhost/127.0.0.1 for development.
 *
 * @param domain - The bare domain string to validate (no protocol)
 * @param allowLocalhost - Whether to accept localhost/127.0.0.1 with optional port
 *
 * @example
 * isValidDomainInput("example.com", false) // true
 * isValidDomainInput("localhost:8080", true) // true
 * isValidDomainInput("localhost:8080", false) // false
 * isValidDomainInput("-invalid.com", false) // false
 */
export function isValidDomainInput(domain: string, allowLocalhost: boolean): boolean {
    if (!domain || domain.trim().length === 0) {
        return false;
    }
    const trimmed = domain.trim();
    if (allowLocalhost && LOCALHOST_REGEX.test(trimmed)) {
        return true;
    }
    return DOMAIN_REGEX.test(trimmed);
}

/**
 * Checks if a string is a valid URL using URL.canParse when available,
 * falling back to try-catch with URL constructor for older browsers.
 *
 * @param url - The string to validate as a URL
 * @returns true if the string is a valid URL, false otherwise
 *
 * @example
 * isValidURL("https://example.com") // true
 * isValidURL("http://example.com") // true
 * isValidURL("git@github.com:user/repo.git") // true (SSH URLs)
 * isValidURL("not a url") // false
 */
export function isValidURL(url: string): boolean {
    // Check if URL.canParse is available (modern browsers)
    if (typeof URL.canParse === "function") {
        try {
            return URL.canParse(url);
        } catch {
            // If URL.canParse throws an error, fall back to try-catch method
            try {
                new URL(url);
                return true;
            } catch {
                return false;
            }
        }
    }

    // Fallback for older browsers that don't support URL.canParse
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * Validates if a string is a valid HTTPS URL.
 * Used for validating URLs that must use HTTPS protocol (e.g., SSO issuer URLs).
 *
 * @param url - The URL string to validate
 * @returns true if the URL is valid and uses HTTPS protocol, false otherwise
 *
 * @example
 * isValidHTTPSURL("https://example.com") // true
 * isValidHTTPSURL("https://sso.example.com/oauth2/default") // true
 * isValidHTTPSURL("https://example.com:8080") // true
 * isValidHTTPSURL("http://example.com") // false (not HTTPS)
 * isValidHTTPSURL("example.com") // false (no protocol)
 */
export function isValidHTTPSURL(url: string): boolean {
    if (!url || url.trim().length === 0) {
        return false;
    }

    // Regex for HTTPS URLs with optional port and path
    // - Must start with https://
    // - Host can be domain or IP address
    // - Optional port (:8080)
    // - Optional path (/path/to/resource)
    const httpsUrlRegex =
        /^https:\/\/(?:[a-zA-Z0-9.-]+|\d{1,3}(?:\.\d{1,3}){3})(?::\d{1,5})?(?:\/[a-zA-Z0-9._~:/?#[\]@!$&'()*+,;=-]*)*\/?$/;

    return httpsUrlRegex.test(url.trim());
}

/**
 * Validates if a string is a valid server URL for Gitpod configuration.
 * Requires HTTPS for all URLs except localhost (for development).
 *
 * @param url - The URL string to validate
 * @returns true if the URL is valid (HTTPS or localhost HTTP), false otherwise
 *
 * @example
 * isValidServerURL("https://example.com") // true
 * isValidServerURL("https://gitpod.example.com") // true
 * isValidServerURL("http://localhost:3000") // true (localhost exception)
 * isValidServerURL("http://127.0.0.1:8080") // true (localhost exception)
 * isValidServerURL("http://example.com") // false (HTTP not allowed for non-localhost)
 */
export function isValidServerURL(url: string): boolean {
    if (!url || url.trim().length === 0) {
        return false;
    }

    try {
        const parsed = new URL(url.trim());

        // Allow HTTPS for any host
        if (parsed.protocol === "https:") {
            return true;
        }

        // Allow HTTP only for localhost (development)
        if (parsed.protocol === "http:") {
            const hostname = parsed.hostname.toLowerCase();
            return hostname === "localhost" || hostname === "127.0.0.1";
        }

        return false;
    } catch {
        // Invalid URL
        return false;
    }
}

/**
 * Normalizes a domain by removing protocol and trailing slashes.
 * Useful for ensuring domains are stored without protocol prefixes.
 *
 * @param domain - The domain string to normalize
 * @returns The normalized domain without protocol or trailing slashes
 *
 * @example
 * normalizeDomain("https://example.com") // "example.com"
 * normalizeDomain("http://example.com/") // "example.com"
 * normalizeDomain("example.com") // "example.com"
 * normalizeDomain("example.com/path") // "example.com/path"
 */
export function normalizeDomain(domain: string): string {
    if (!domain) {
        return "";
    }
    // Trim first, then remove protocol and trailing slash
    return domain
        .trim()
        .replace(/^https?:\/\//, "")
        .replace(/\/$/, "");
}

/**
 * Checks if a string looks like a valid repository URL.
 * Must be a valid URL with a path containing at least owner/repo segments.
 *
 * @param value - The string to validate
 * @returns true if the string looks like a repository URL
 *
 * @example
 * isValidRepoUrl("https://github.com/gitpod-io/gitpod") // true
 * isValidRepoUrl("https://gitlab.com/group/project") // true
 * isValidRepoUrl("https://github.com/gitpod-io/gitpod/tree/main") // true
 * isValidRepoUrl("https://github.com") // false (no repo path)
 * isValidRepoUrl("not a url") // false
 */
export function isValidRepoUrl(value: string): boolean {
    if (!value) return false;
    try {
        const url = new URL(value);
        // Must have a path with at least owner/repo (e.g., /gitpod-io/gitpod)
        const segments = url.pathname.split("/").filter(Boolean);
        return segments.length >= 2;
    } catch {
        return false;
    }
}
